<script lang="ts">
    import * as Popover from "./ui/popover/index.js";
    import * as Command from "./ui/command/index.js";
    import { Badge } from "./ui/badge/index.js";
    import Tag from "@lucide/svelte/icons/tag";
    import Check from "@lucide/svelte/icons/check";
    import { m } from "../messages.svelte.js";
    import { cn, getClient } from "../utils.js";
    import { locale } from "../locale.svelte.js";
    import type { Res } from "@repo/backend/client";

    type CaseTag = Res["Get case tags"][number];
    type TagId = CaseTag["id"];

    interface Props {
        caseId: string;
        tags: CaseTag[];
        selectedTagIds: TagId[];
        onChanged?: () => void;
        class?: string;
    }

    const { caseId, tags, selectedTagIds, onChanged, class: className }: Props = $props();

    let localTagIds = $state<TagId[]>(selectedTagIds);

    $effect(() => {
        localTagIds = selectedTagIds;
    });

    let open = $state(false);
    const isDE = $derived(locale.current.startsWith("de"));
    const labelOf = $derived((tag: CaseTag) => (isDE ? tag.labelDe : tag.labelEn));

    async function toggleTag(tagId: TagId) {
        const next = localTagIds.includes(tagId)
            ? localTagIds.filter(id => id !== tagId)
            : [...localTagIds, tagId];
        localTagIds = next;
        await getClient()["Set case tags"]({ jsonBody: { caseId, tagIds: next } });
        onChanged?.();
    }
</script>

<div class={cn("flex items-center", className)} onclick={e => e.stopPropagation()}>
    <Popover.Root bind:open>
        {#if localTagIds.length > 0}
            <Popover.Trigger class="flex items-center gap-1 rounded-md p-0.5 transition-colors hover:bg-accent">
                {#each tags.filter(t => localTagIds.includes(t.id)) as tag (tag.id)}
                    <Badge
                        class="whitespace-nowrap text-xs"
                        style="background-color: color-mix(in srgb, {tag.color} 15%, transparent); color: {tag.color}; border-color: color-mix(in srgb, {tag.color} 30%, transparent);"
                    >
                        {labelOf(tag)}
                    </Badge>
                {/each}
            </Popover.Trigger>
        {:else}
            <Popover.Trigger
                class="text-muted-foreground hover:bg-accent hover:text-foreground inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors"
            >
                <Tag class="size-3.5" />
            </Popover.Trigger>
        {/if}
        <Popover.Content class="w-52 p-0" align="start">
            <Command.Root>
                <Command.Input placeholder={m["workbench.searchTags"]()} />
                <Command.List>
                    <Command.Empty>{m["workbench.searchTags"]()}</Command.Empty>
                    <Command.Group>
                        {#each tags as tag (tag.id)}
                            {@const selected = localTagIds.includes(tag.id)}
                            <Command.Item
                                value={tag.id}
                                keywords={[tag.labelEn, tag.labelDe]}
                                onSelect={() => toggleTag(tag.id)}
                            >
                                <div
                                    class={cn(
                                        "border-primary mr-1 flex h-4 w-4 items-center justify-center rounded-sm border",
                                        selected ? "bg-primary text-primary-foreground" : "opacity-50",
                                    )}
                                >
                                    {#if selected}
                                        <Check class="size-3" />
                                    {/if}
                                </div>
                                <span
                                    class="h-3 w-3 shrink-0 rounded-full"
                                    style="background-color: {tag.color};"
                                ></span>
                                {labelOf(tag)}
                            </Command.Item>
                        {/each}
                    </Command.Group>
                </Command.List>
            </Command.Root>
        </Popover.Content>
    </Popover.Root>
</div>
